<template>
  <transition appear>
    <div class="owal-training-tool-filter container">
      <camera-picker
        :show="showCameraPicker"
        @change="updateCameraId"
        @close="showCameraPicker = false"
        />
      <person-picker
        :show="showPersonPicker"
        :change-handler="updateFaceId"
        @close="showPersonPicker = false"
        />
      <span class="owal-training-tool-filter__input">
        <label> date & time </label>
        <input
          class="date"
          type="date"
          :value="date"
          @change="updateDate"
          >
        <input
          class="time"
          type="time"
          :value="time"
          @change="updateTime"
          >
        <span
          class="owal-training-tool-filter__picker-auxiliary-button"
          @click="updateDateAndTime"
          >
          Now
        </span>
      </span>
      <span class="owal-training-tool-filter__input camera">
        <label> camera </label>
        <span class="owal-training-tool-filter picker container" @click="showCameraPicker = true">
          <span class="owal-training-tool-filter picker value">
            {{ cameraName }}
          </span>
        </span>
        <span class="owal-training-tool-filter__picker-auxiliary-button" @click="updateCameraId(null)">
          Any
        </span>
      </span>
      <span class="owal-training-tool-filter__input person">
        <label>
          person
        </label>
        <span class="owal-training-tool-filter picker container" @click="showPersonPicker = true">
          <span class="owal-training-tool-filter picker value">
            {{ personName }}
          </span>
        </span>
        <span class="owal-training-tool-filter__picker-auxiliary-button" @click="updateFaceId([])">
          Any
        </span>
      </span>
      <span class="owal-training-tool-filter__input model">
        <label>
          Model
        </label>
        <select
          class="owal training tool select"
          style="width: 26.35rem"
          id="model-picker"
          @input="stageChange('modelKey', $event, (event) => {
            return event.target.selectedOptions[0].label
          })"
          >
          <optgroup label="Available Models">
            <option
              v-for="model in this.sortedModels"
              :key="model.key"
              :value="model.label"
              :selected="model.key === filteringModel.key ? 'selected' : ''"
              >
              {{ model.label }} | top 1: {{ model.top1 }} | top 5: {{ model.top5 }}
            </option>
          </optgroup>
        </select>
        <span class="owal-training-tool-filter__picker-auxiliary-button" @click="clearModelPicker">
          Current
        </span>
      </span>
      <span class="owal-training-tool-filter__input accuracy">
        <label>
          person accuracy
        </label>
        <select
          class="owal training tool select"
          @input="stageChange('confidencePredicate', $event, (event) => {
            const collection = event.target.selectedOptions
            const value = Array.from(collection)[0].value
            return value
          })"
          >
          <optgroup
            label="Currently Selected"
            >
            <option
              :key="selectedAccuracyOption.value"
              :value="selectedAccuracyOption.value"
              >
              {{ this.selectedAccuracyOption.displayName }}
            </option>
          </optgroup>
          <optgroup
            label="Compare By"
            >
            <option
              v-for="option in this.accuracyOptions"
              :key="option.value"
              :value="option.value"
              >
              {{ option.displayName }}
            </option>
          </optgroup>
        </select>
        <input
          type="number"
          placeholder="0.01"
          step="0.01"
          min="0"
          max="1"
          :value="this.stagedOrSavedValue('confidence')"
          @input="stageChange('confidence', $event.target.value)"
        >
        <span
          class="owal-training-tool-filter__picker-auxiliary-button"
          @click="clearConfidence"
        >
          Any
        </span>
      </span>
      <span
        class="owal-training-tool-filter__input minimum-size"
      >
        <label>
          minimum face height
        </label>
        <input
          type="text"
          :value="this.stagedOrSavedValue('faceHeight')"
          placeholder="0"
          @change="stageChange('faceHeight', $event.target.value, preprocessMinimumFaceHeight)"
        >
        px
      </span>
      <span
        class="owal-training-tool-filter__input"
      >
        <label>
          shapes
        </label>
        <input
          type="checkbox"
          value="checked"
          :checked="this.stagedOrSavedValue('shapesOnFaces')"
          @click="stageChange('shapesOnFaces', $event.target.checked, updateCLAHEFilterBasedOnShapes)"
        >
        <label
          v-if="this.showCLAHEFilter === true"
        >
          CLAHE
        </label>
        <input
          v-if="this.showCLAHEFilter === true"
          type="checkbox"
          value="checked"
          :checked="this.stagedOrSavedValue('clahe')"
          @click="stageChange('clahe', $event.target.checked)"
        >
      </span>
      <span class="owal-training-tool-filter buttons">
        <owal-panel-header-button
          title="Cancel"
          identifier="cancel"
          @button-click="handleButtonClick"
        />
        <owal-panel-header-button
          title="Update Filters"
          identifier="save"
          @button-click="handleButtonClick"
        />
      </span>
    </div>
  </transition>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import OwalPanelHeaderButton from '@/libraries/owal-ui/panels/OwalPanelHeaderButton'
import CameraPicker from '@/components/shared/CameraPicker'
import PersonPicker from '@/components/shared/PersonPicker'

export default {
  name: 'OwalTrainingToolFilter',
  components: {
    OwalPanelHeaderButton,
    CameraPicker,
    PersonPicker,
  },
  data () {
    return {
      dateFormat: 'YYYY-MM-DD',
      timeFormat: 'HH:mm:ss',
      tokenFormat: 'ddd, DD MMM YYYY HH:mm:ss',
      pendingChanges: {},
      showCameraPicker: false,
      showPersonPicker: false,
      showModelPicker: false,
      cameraName: 'Any Camera',
      personName: 'Any Person',
      shapesOnFaces: false,
      /**
       *  CLAHE is short for Contrast limited adaptive histogram equalization
       *  - via  https://en.wikipedia.org/wiki/Adaptive_histogram_equalization
       */
      clahe: false,
      showCLAHEFilter: false,
      displayMoment: moment().local(),
    }
  },
  mounted () {
    this.updateEditing(true)
    this.updateCameraName(this.filters.cameraUUID)
    this.updateFaceName(this.filters.faceUUIDs)
    this.stageChangeForKeyToValue('date', this.filters.date)
    this.getModelsForCurrentDomain()
    if (!this.filteringModel) {
      this.setFilteringModel(this.currentModel)
    }
    this.displayMoment = moment(this.filters.date).local() || moment().local()
    this.showCLAHEFilter = this.stagedOrSavedValue('shapesOnFaces') === true
  },
  beforeDestroy () {
    this.updateEditing(false)
    this.pendingChanges = {}
  },
  computed: {
    ...mapGetters({
      filters: 'trainingTool/filters',
      isEditing: 'trainingTool/isEditing',
      cameraForId: 'cameraForUUID',
      frontendFaces: 'frontendFaces',
      sortedModels: 'trainingTool/sortedModels',
      filteringModel: 'trainingTool/filteringModel',
      currentModel: 'trainingTool/currentModel',
    }),
    /**
     * Accuracy operators
     * These values should match Python 2's syntax: https://docs.python.org/2/library/operator.html
     */
    accuracyOptions () {
      return [{
        value: 'ge',
        displayName: 'At Least (>=)',
      },
      {
        value: 'gt',
        displayName: 'More Than (>)',
      },
      {
        value: 'eq',
        displayName: 'Exactly (=)',
      },
      {
        value: 'le',
        displayName: 'At Most (<=)',
      },
      {
        value: 'lt',
        displayName: 'Less Than (<)',
      }]
    },
    selectedAccuracyOption () {
      const matchingOption = this.accuracyOptions.filter(option => option.value === this.stagedOrSavedValue('confidencePredicate'))

      if (!matchingOption || matchingOption.length === 0) {
        return {
          displayName: '-',
          value: '',
        }
      }

      return matchingOption[0]
    },
    deselectedAccuracyOptions () {
      return this.accuracyOptions.filter(option => option.value !== this.stagedOrSavedValue('confidencePredicate'))
    },
    /**
     * @returns The local moment with formatting for the HTML5 date element.
     */
    date: {
      get: function () {
        return this.displayMoment.format(this.dateFormat)
      },
      set (value) {
        this.updateDate({ target: { value } })
      },
    },
    /**
     * @returns The local moment with formatting for the HTML5 time element.
     */
    time: {
      get () {
        return this.displayMoment.format(this.timeFormat)
      },
      set (value) {
        this.updateTime({ target: { value } })
      },
    },
    /**
     * The UUID to display - either the pending one, or if there is no change pending, the saved one.
     */
    cameraUUID () {
      let cameraId = this.filters.cameraUUID

      if (this.pendingChanges && this.pendingChanges.cameraUUID) {
        cameraId = this.pendingChanges.cameraUUID
      }
      return cameraId
    },
  },
  methods: {
    ...mapActions({
      updateFilters: 'trainingTool/updateFilters',
      updateEditing: 'trainingTool/updateEditing',
      getModelsForCurrentDomain: 'trainingTool/getModelsForCurrentDomain',
      getCurrentModel: 'trainingTool/getCurrentModel',
    }),
    ...mapMutations({
      setFilteringModel: 'trainingTool/setFilteringModel',
    }),
    clearModelPicker () {
      this.setFilteringModel(this.currentModel)
      this.stageChange('modelKey', this.currentModel.label)
    },
    updateDateAndTime () {
      const dateFormat = this.dateFormat
      const timeFormat = this.timeFormat
      const newMoment = moment().local()
      this.updateDate({
        target: {
          value: newMoment.format(dateFormat),
        },
      })
      this.updateTime({
        target: {
          value: newMoment.format(timeFormat),
        },
      })
    },
    /**
     * The value for a given key,.
     */
    stagedOrSavedValue (key) {
      if (key in this.pendingChanges) {
        // console.debug(`Returning pending value for ${key}`)
        return this.pendingChanges[key]
      }
      // console.debug(`Returning saved value for ${key}`)
      return this.filters[key]
    },
    /**
     * Handle when cancel or save buttons are clicked.
     */
    handleButtonClick (identifier) {
      if (identifier === 'save') {
        this.commitEdits()
        this.$emit('save')
      } else if (identifier === 'cancel') {
        this.resetEdits()
        this.$emit('cancel')
      }
      this.$emit('close')
    },
    /**
     * Takes a time string in the format hh:mm:ss or hh:mm and uses it to set the time.
     */
    updateTime (event) {
      const seperator = ':'
      let value = event.target.value
      const fields = ['hour', 'minute', 'second']

      const lengthOfTimeFormatWithoutSeconds = 5 // 'hh:mm' is five chars long.
      if (value.length === lengthOfTimeFormatWithoutSeconds) {
        // If no seconds are returned by browser, assume 0 seconds.
        value = `${value}:00`
      }
      this.updateMoment(value, seperator, fields)
    },
    /**
     * Takes a date string in the format YYYY-MM-DD and uses it to set the date.
     */
    updateDate (event) {
      const separator = '-'
      const value = event.target.value
      const fields = ['year', 'month', 'date']
      this.updateMoment(value, separator, fields)
    },
    /**
     * This method takes a string, a seperator and the names of the fields in the string.
     * It uses those to update a date as appropriate.
     *
     * @param value A string, such as '2007-06-29' or '09:41'
     * @param separator The separator, such as '-' or ':'
     * @param fields The names of the fields in the string, per the moment.js documentation.
     */
    updateMoment (value, separator, fields) {
      const components = value.split(separator)

      if (components.length < fields.length) {
        this.dateInMilliseconds = this.dateInMilliseconds
        return
      }

      const tempMoment = this.displayMoment
      // console.log(`Before: ${tempMoment.format()}`)
      for (const field of fields) {
        const index = fields.indexOf(field)
        let newValue = parseInt(components[index], 10)
        if (field === 'month') {
          newValue = newValue - 1
        }
        tempMoment.set(field, newValue)
      }
      const newDate = tempMoment.valueOf()
      this.displayMoment = moment(newDate).local()
      this.stageChangeForKeyToValue('date', newDate)
    },
    /**
     * Stages changes to the minimum face height.
     * @param event The HTML DOM event.
     */
    preprocessMinimumFaceHeight (value) {
      let output = parseInt(value, 10) // parse the input string as a base 10 number.

      if (isNaN(output)) {
        // console.log(`value: ${event.target.value} is not a number.`)
        output = 0
      }
      return output
    },
    /**
     * Stage changes for a given filter.
     */
    stageChange (key, value, preprocessor) {
      const finalValue = preprocessor ? preprocessor(value) : value
      // console.log(`Final value: ${finalValue}`)
      this.stageChangeForKeyToValue(key, finalValue)
    },
    /**
     * Stages changes to the camera.
     */
    updateCameraId (cameraId) {
      this.stageChangeForKeyToValue('cameraUUID', cameraId)
      this.updateCameraName(cameraId)
      this.showCameraPicker = false
    },
    /**
     * Stages changes to the person.
     */
    updateFaceId (newValue) {
      let faceIds = newValue
      if (!Array.isArray(faceIds)) {
        faceIds = []
      } else {
        faceIds = newValue.filter(obj => typeof obj === 'string').filter(string => string.length > 0)
      }
      this.stageChangeForKeyToValue('faceUUIDs', faceIds)
      this.updateFaceName(faceIds)
      this.showPersonPicker = false
    },
    /**
     * Change the filter for the given key to the new value, then emit the change.
     */
    stageChangeForKeyToValue (key, value) {
      this.pendingChanges[key] = value
    },
    /**
     * Updates the working filters object based on the contents of the store.
     */
    resetEdits () {
      this.pendingChanges = {}
      this.updateEditing(false)
    },
    /**
     * Commits the changes to the store and resets pending changes.
     */
    commitEdits () {
      let filters = this.pendingChanges
      // console.debug(`OwalTrainingTool: ${JSON.stringify(filters, null, 2)}`)
      this.updateFilters(filters)
      this.updateEditing(false)
      this.pendingChanges = {}
    },
    /**
     * The name of the camera to filter by, for display purposes only.
     */
    updateCameraName (uuid) {
      let cameraId = uuid
      let name = 'Any Camera'

      if (cameraId) {
        const matchingCamera = this.cameraForId(cameraId)

        if (matchingCamera) {
          name = matchingCamera.description || `Unknown (${cameraId})`
        }
      }
      this.cameraName = name
    },
    /**
     * Updates the display name of faces
     */
    updateFaceName (uuids) {
      let faceIds = uuids
      let name = 'Any Person'

      if (!uuids) {
        return name
      }

      if (!Array.isArray(uuids)) {
        faceIds = [uuids]
      }

      if (faceIds.length > 0) {
        name = faceIds.map(id => this.nameForFaceId(id)).join(', ')
      }
      this.personName = name
    },
    /**
     * Fetches the display name for a given UUID.
     */
    nameForFaceId (faceId) {
      const faces = this.frontendFaces.filter(face => face.frontend_face_uuid === faceId)
      const defaultName = `Unknown (${faceId})`

      if (faces.length === 1) {
        return faces[0].person_name
      }

      return defaultName
    },
    clearConfidence () {
      this.stageChange('confidencePredicate', null)
      this.stageChange('confidence', 0)
    },
    updateCLAHEFilterBasedOnShapes (value) {
      this.showCLAHEFilter = value
      // console.debug(`Show shape: ${value}`)
      return value
    },
  },
}

</script>

<style scoped>
.owal-training-tool-filter.container {
  display: flex;
  flex-direction: column;
  padding: .9375rem;
  box-sizing: border-box;
  width: 100%;
  background: #F3F4F5;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}

.owal-training-tool-filter.picker.container,
.owal-training-tool-filter.container input,
.owal-training-tool-filter.container select {
  background: #F0F0F0;
  border: 2px solid #E0E0E0;
  border-radius: 5px;

  font-family: 'Lato-Regular', sans-serif;
  font-size: 1.28571rem;
  color: #324057;
  letter-spacing: 0;
  padding: 0.87571rem 0.64285rem;
  margin: 0.2857142857rem .1785714286rem;
  height: 3.6rem;
  flex-grow: 0;
  text-align: center;
}

.owal-training-tool-filter__input.minimum-size input {
  width: 4rem;
}

.owal-training-tool-filter__input input {
  width: 13rem;
}

.owal-training-tool-filter__input.accuracy input {
  width: 5rem;
}

/* date & time, face height */
.owal-training-tool-filter__input {
  font-family: 'Lato-Italic';
  font-size: 1rem;
  color: #8492A6;
  letter-spacing: 0;
  display: flex;
  align-content: center;
  align-items: center;
  width: 100%;
}

.owal-training-tool-filter__input label {
  text-align: right;
  width: 10rem;
  flex-shrink: 0;
  box-sizing: border-box;
  margin: 0.2857142857rem .1785714286rem;
}

.owal-training-tool-filter.buttons {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  border-top: 1px solid rgba(132, 146, 166, 0.2);
  margin-top: 0.5rem;
  padding-top: 0.5rem;
}

/* --- Camera Picker --- */
.owal-training-tool-filter.picker.container {
  width: 26.35rem;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  cursor: pointer;
}

.owal-training-tool-filter__picker-auxiliary-button {
  font-size: 0.9rem;
  transition: all 300ms ease;
  text-align: center;
  flex-shrink: 0;
  font-family: 'Lato', 'Avenir', sans-serif;
  background-color: rgb(183, 189, 197);
  color: #F3F4F5;
  border: 2px solid #F3F4F5;
  padding: 0.25rem 0.35rem;
  margin: 0 0.5rem;
  border-radius: 0.5rem;
  cursor: pointer;
  font-weight: bold;
}

.owal-training-tool-filter__picker-auxiliary-button:hover {
  transform: scale(1.1);
  opacity: 0.8;
}

.owal-training-tool-filter.picker.value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  flex-grow: 1;
  flex-shrink: none;
}

/* --- Animations --- */
.v-enter-active, .v-leave-active {
  transition: all 300ms ease-in-out;
}

.v-enter, .v-leave-to {
  opacity: 0;
  height: 0;
}

.v-enter-to, .v-leave {
  opacity: 1;
  height: auto;
}

</style>
